/**
 * ProfessionalHub — profiles list + top-level gate checks.
 * Sub-views (playbooks, editor, session, stats) are separate routes.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiBarChart2, FiList, FiEdit2, FiTrash2 } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import { useAuth } from '../contexts/AuthContext'
import { professional as api, type ProfessionalProfile } from '../api/client'
import PageHeader from '../components/PageHeader'

interface Props {
  onBack:             () => void
  onGoToSubscription: () => void
}

export default function ProfessionalHub({ onBack, onGoToSubscription }: Props) {
  const navigate          = useNavigate()
  const { t }             = useTranslation()
  const { user } = useAuth()
  const p                 = t.professional

  const isPremium      = user?.tier === 'PREMIUM'
  const isProfessional = user?.isProfessional ?? false

  const [profiles, setProfiles]   = useState<ProfessionalProfile[]>([])
  const [loading, setLoading]     = useState(false)

  // Profile form state
  const [showProfileForm, setShowProfileForm]     = useState(false)
  const [profileFormName, setProfileFormName]     = useState('')
  const [profileFormNotes, setProfileFormNotes]   = useState('')
  const [profileFormPin, setProfileFormPin]       = useState('')
  const [editingProfile, setEditingProfile]       = useState<ProfessionalProfile | null>(null)
  const [savingProfile, setSavingProfile]         = useState(false)
  const [pinError, setPinError]                   = useState(false)
  const [confirmDelete, setConfirmDelete]         = useState<string | null>(null)

  useEffect(() => {
    if (!isPremium || !isProfessional) return
    setLoading(true)
    api.listProfiles()
      .then(setProfiles)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isPremium, isProfessional])

  // ── Profile CRUD ──────────────────────────────────────────────

  const openCreateProfile = () => {
    setEditingProfile(null)
    setProfileFormName('')
    setProfileFormNotes('')
    setProfileFormPin('')
    setPinError(false)
    setShowProfileForm(true)
  }

  const openEditProfile = (profile: ProfessionalProfile) => {
    setEditingProfile(profile)
    setProfileFormName(profile.name)
    setProfileFormNotes(profile.notes ?? '')
    setProfileFormPin('')
    setPinError(false)
    setShowProfileForm(true)
  }

  const saveProfile = async () => {
    if (!profileFormName) return
    if (!editingProfile && profileFormPin.length !== 4) {
      setPinError(true)
      return
    }
    setSavingProfile(true)
    try {
      if (editingProfile) {
        const updated = await api.updateProfile(editingProfile.id, {
          name:  profileFormName,
          notes: profileFormNotes || null,
          ...(profileFormPin.length === 4 ? { pin: profileFormPin } : {}),
        })
        setProfiles(prev => prev.map(pr => pr.id === updated.id ? updated : pr))
      } else {
        const created = await api.createProfile({
          name:  profileFormName,
          notes: profileFormNotes || undefined,
          pin:   profileFormPin,
        })
        setProfiles(prev => [...prev, created])
      }
      setShowProfileForm(false)
    } catch {
      // ignore
    } finally {
      setSavingProfile(false)
    }
  }

  const deleteProfile = async (id: string) => {
    await api.deleteProfile(id).catch(() => {})
    setProfiles(prev => prev.filter(pr => pr.id !== id))
    setConfirmDelete(null)
  }

  // ── Gate: not premium ─────────────────────────────────────────

  if (!isPremium) {
    return (
      <div style={s.root}>
        <PageHeader onBack={onBack} backLabel={p.back} title={p.hubTitle} />
        <div style={s.inner}>
          <div style={s.gateCard}>
            <div style={s.gateEmoji}>👑</div>
            <h1 style={s.gateTitle}>{p.premiumRequired}</h1>
            <p style={s.gateDesc}>{p.premiumRequiredDesc}</p>
            <button style={s.gateBtn} onClick={onGoToSubscription}>{p.goToPremium}</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Gate: premium but not yet enabled ─────────────────────────

  if (!isProfessional) {
    return (
      <div style={s.root}>
        <PageHeader onBack={onBack} backLabel={p.back} title={p.hubTitle} />
        <div style={s.inner}>
          <p style={s.subtitle}>{p.hubSubtitle}</p>
          <div style={s.enableCard}>
            <p style={s.enableDesc}>{p.enableDesc}</p>
            <button style={s.primaryBtn} onClick={onBack}>
              {p.back}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Main: profiles list ───────────────────────────────────────

  return (
    <div style={s.root}>
      <PageHeader onBack={onBack} backLabel={p.back} title={p.hubTitle} />
      <div style={s.inner}>

        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>{p.profilesTitle}</h2>
          <button style={s.secondaryBtn} onClick={openCreateProfile}>{p.newProfile}</button>
        </div>

        {loading && <p style={s.hint}>…</p>}
        {!loading && profiles.length === 0 && <p style={s.hint}>{p.noProfiles}</p>}

        {profiles.map(profile => (
          <div key={profile.id} style={s.card}>
            <div style={s.cardMain}>
              <span style={s.cardEmoji}>👤</span>
              <div style={s.cardInfo}>
                <div style={s.cardName}>{profile.name}</div>
                {profile.notes && <div style={s.cardNote}>{profile.notes}</div>}
              </div>
            </div>
            <div style={s.cardActions}>
              <button
                style={s.actionBtn}
                onClick={() => navigate(
                  `/professional/profiles/${profile.id}/stats`,
                  { state: { profile } },
                )}
                aria-label="Estadísticas"
              >
                <FiBarChart2 size={16} />
              </button>
              <button
                style={s.actionBtn}
                onClick={() => navigate(
                  `/professional/profiles/${profile.id}/playbooks`,
                  { state: { profile } },
                )}
                aria-label="Ver rutinas"
              >
                <FiList size={16} />
              </button>
              <button style={s.actionBtn} onClick={() => openEditProfile(profile)} aria-label="Editar">
                <FiEdit2 size={16} />
              </button>
              <button
                style={{ ...s.actionBtn, ...s.actionBtnDanger }}
                onClick={() => setConfirmDelete(profile.id)}
                aria-label="Eliminar"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          </div>
        ))}

        {/* ── Profile form modal ── */}
        {showProfileForm && (
          <div style={s.overlay}>
            <div style={s.modal}>
              <h2 style={s.modalTitle}>{editingProfile ? p.editProfile : p.createProfile}</h2>

              <label style={s.fieldLabel}>{p.profileName}</label>
              <input
                style={s.input}
                value={profileFormName}
                onChange={e => setProfileFormName(e.target.value)}
                placeholder={p.profileName}
                maxLength={80}
                autoFocus
              />

              <label style={s.fieldLabel}>{p.profileNotes}</label>
              <textarea
                style={{ ...s.input, minHeight: '72px' }}
                value={profileFormNotes}
                onChange={e => setProfileFormNotes(e.target.value)}
                placeholder={p.profileNotes}
                maxLength={500}
              />

              <label style={{ ...s.fieldLabel, ...(pinError ? { color: '#c0392b' } : {}) }}>
                {editingProfile ? `${p.profilePin} (${p.profilePinHint})` : p.profilePin}
              </label>
              <input
                style={{
                  ...s.input,
                  ...(pinError ? { borderColor: '#c0392b', background: '#fff5f5' } : {}),
                }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={profileFormPin}
                onChange={e => {
                  setProfileFormPin(e.target.value.replace(/\D/g, '').slice(0, 4))
                  setPinError(false)
                }}
                placeholder={editingProfile ? '(sin cambios)' : '4 dígitos'}
                maxLength={4}
              />
              {pinError ? (
                <p style={{ fontSize: '0.875rem', color: '#c0392b', margin: '-4px 0 0' }}>
                  El PIN debe tener exactamente 4 dígitos.
                </p>
              ) : (
                <p style={s.fieldHint}>{p.profilePinHint}</p>
              )}

              <div style={s.modalActions}>
                <button style={s.ghostBtn} onClick={() => setShowProfileForm(false)}>{p.cancel}</button>
                <button
                  style={{ ...s.primaryBtn, ...(savingProfile ? s.primaryBtnDisabled : {}) }}
                  onClick={saveProfile}
                  disabled={savingProfile || !profileFormName}
                >
                  {savingProfile ? '…' : p.saveProfile}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete profile confirm modal ── */}
        {confirmDelete && (
          <div style={s.overlay}>
            <div style={s.modal}>
              <h2 style={s.modalTitle}>{p.deleteProfile}</h2>
              <p style={s.modalDesc}>{p.confirmDelete}</p>
              <div style={s.modalActions}>
                <button style={s.ghostBtn} onClick={() => setConfirmDelete(null)}>{p.cancel}</button>
                <button
                  style={{ ...s.primaryBtn, background: '#c0392b' }}
                  onClick={() => deleteProfile(confirmDelete)}
                >
                  {p.deleteProfile}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:     '100vh',
    background:    '#FAF6EF',
  },
  inner: {
    maxWidth:      '640px',
    margin:        '0 auto',
    padding:       '24px 16px 40px',
    display:       'flex',
    flexDirection: 'column',
    gap:           '16px',
  },
  subtitle: {
    fontSize: '1rem',
    color:    '#666',
    margin:   0,
  },
  sectionHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color:      '#2D3A2E',
    margin:     0,
  },
  hint: {
    fontSize: '1rem',
    color:     '#888',
    textAlign: 'center',
    padding:   '16px 0',
  },
  card: {
    background:   '#fff',
    borderRadius: '16px',
    border:       '1px solid #e8e0d5',
    padding:      '14px 16px',
    display:      'flex',
    alignItems:   'center',
    gap:          '12px',
  },
  cardMain: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
    flex:       1,
    minWidth:   0,
  },
  cardEmoji: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  cardInfo: {
    flex:     1,
    minWidth: 0,
  },
  cardName: {
    fontSize: '1.0625rem',
    fontWeight:   600,
    color:        '#2D3A2E',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    whiteSpace:   'nowrap',
  },
  cardNote: {
    fontSize: '0.875rem',
    color:     '#888',
    marginTop: '2px',
  },
  cardActions: {
    display:    'flex',
    gap:        '6px',
    flexShrink: 0,
  },
  actionBtn: {
    background:     'none',
    border:         '1px solid #e0d8cf',
    borderRadius:   '10px',
    padding:        '6px 10px',
    cursor:         'pointer',
    minHeight:      '40px',
    minWidth:       '40px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    color:          '#555',
  },
  actionBtnDanger: {
    borderColor: '#f5c6c2',
    color:       '#c0392b',
  },
  primaryBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '12px',
    padding:      '12px 24px',
    fontSize: '1rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '48px',
  },
  primaryBtnDisabled: {
    opacity: 0.6,
    cursor:  'not-allowed',
  },
  secondaryBtn: {
    background:   '#EAF3EB',
    color:        '#2D5A32',
    border:       '1px solid #c8e0ca',
    borderRadius: '12px',
    padding:      '10px 18px',
    fontSize: '0.9375rem',
    fontWeight:   600,
    cursor:       'pointer',
    minHeight:    '48px',
  },
  ghostBtn: {
    background:   'none',
    color:        '#666',
    border:       '1px solid #ddd',
    borderRadius: '12px',
    padding:      '12px 24px',
    fontSize: '1rem',
    cursor:       'pointer',
    minHeight:    '48px',
  },
  enableCard: {
    background:    '#fff',
    borderRadius:  '16px',
    border:        '1px solid #e8e0d5',
    padding:       '24px',
    display:       'flex',
    flexDirection: 'column',
    gap:           '16px',
  },
  enableDesc: {
    fontSize: '1rem',
    color:    '#555',
    margin:   0,
  },
  gateCard: {
    background:    '#fff',
    borderRadius:  '20px',
    border:        '1px solid #e8e0d5',
    padding:       '40px 24px',
    textAlign:     'center',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           '16px',
  },
  gateEmoji: {
    fontSize: '3.5rem',
  },
  gateTitle: {
    fontSize: '1.375rem',
    fontWeight: 800,
    color:      '#2D3A2E',
    margin:     0,
  },
  gateDesc: {
    fontSize: '1rem',
    color:    '#666',
    margin:   0,
  },
  gateBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '12px',
    padding:      '14px 32px',
    fontSize: '1.0625rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '52px',
    marginTop:    '8px',
  },
  overlay: {
    position:       'fixed',
    inset:          0,
    background:     'rgba(0,0,0,0.5)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         999,
    padding:        '16px',
  },
  modal: {
    background:    '#fff',
    borderRadius:  '20px',
    padding:       '28px 24px',
    maxWidth:      '400px',
    width:         '100%',
    display:       'flex',
    flexDirection: 'column',
    gap:           '12px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color:      '#2D3A2E',
    margin:     0,
  },
  modalDesc: {
    fontSize: '1rem',
    color:    '#555',
    margin:   0,
  },
  modalActions: {
    display:        'flex',
    justifyContent: 'flex-end',
    gap:            '10px',
    marginTop:      '4px',
  },
  fieldLabel: {
    fontSize: '0.875rem',
    fontWeight:   600,
    color:        '#444',
    marginBottom: '-4px',
  },
  fieldHint: {
    fontSize: '0.875rem',
    color:    '#888',
    margin:   '-4px 0 0',
  },
  input: {
    fontSize: '1rem',
    padding:      '12px',
    borderRadius: '10px',
    border:       '1px solid #ddd',
    background:   '#FAF6EF',
    width:        '100%',
    boxSizing:    'border-box',
    minHeight:    '48px',
  },
}
